import React, { FunctionComponent, useCallback, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'

import { CSSObject } from 'styled-components'
import { useTheme } from 'styled-components/native'

import { ExternalLinkIcon } from '../../atoms/icons/ExternalLinkIcon'
import { PrimaryButton } from '../../atoms/primaryButton/PrimaryButton'
import {
  AuroraCareOption,
  AuroraCareOptionsMetadata,
  isGridSupported,
} from '../../careOptionsMetadata/auroraCareOptionsMetadata'
import { useGetResponsiveAssets } from '../../hooks/useGetResponsiveAssets'
import { ProgramDetailsContent, ProgramDetailsModal } from '../../organisms/programDetailsModal/ProgramDetailsModal'
import { TitleSize } from '../../styles/typeStyles'
import { CareOptionsGridSlotSize } from '../../utils/careOptions/gridLayout'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { AuroraHeroCard } from '../auroraHeroCard/AuroraHeroCard'
import { CompactImageCard } from '../compactImageCard/CompactImageCard'

const GRID_SLOT_TITLE_STYLE: CSSObject = { flexShrink: 1 }

const GRID_SLOT_TITLE_SIZES: Partial<Record<CareOptionsGridSlotSize, TitleSize>> = {
  [CareOptionsGridSlotSize.SECONDARY_TALL]: TitleSize.SMALL,
  [CareOptionsGridSlotSize.SECONDARY_SHORT]: TitleSize.SMALL,
  [CareOptionsGridSlotSize.TERTIARY]: TitleSize.SMALL,
  [CareOptionsGridSlotSize.MOBILE_FULL]: TitleSize.XSMALL,
  [CareOptionsGridSlotSize.MOBILE_SMALL]: TitleSize.XSMALL,
}
const getGridSlotStyles = (
  spacing: ThemeType['spacing'],
): Partial<
  Record<
    CareOptionsGridSlotSize,
    {
      cardContainerStyle: CSSObject
      imageContainerStyle: CSSObject
      titleContainerStyle: CSSObject
      contentContainerStyle?: CSSObject
    }
  >
> => ({
  [CareOptionsGridSlotSize.SECONDARY_TALL]: {
    cardContainerStyle: { maxWidth: '464px', height: '482px' },
    imageContainerStyle: { height: '406px' },
    titleContainerStyle: GRID_SLOT_TITLE_STYLE,
  },
  [CareOptionsGridSlotSize.SECONDARY_SHORT]: {
    cardContainerStyle: { maxWidth: '464px', height: '233px' },
    imageContainerStyle: { height: '157px' },
    titleContainerStyle: GRID_SLOT_TITLE_STYLE,
  },
  [CareOptionsGridSlotSize.TERTIARY]: {
    cardContainerStyle: { maxWidth: '224px', height: '220px' },
    imageContainerStyle: { height: '130px' },
    titleContainerStyle: GRID_SLOT_TITLE_STYLE,
    contentContainerStyle: { padding: spacing['20px'] },
  },
  [CareOptionsGridSlotSize.MOBILE_FULL]: {
    cardContainerStyle: {},
    imageContainerStyle: { height: '164px' },
    titleContainerStyle: GRID_SLOT_TITLE_STYLE,
  },
  [CareOptionsGridSlotSize.MOBILE_SMALL]: {
    cardContainerStyle: { height: '168px' },
    imageContainerStyle: { height: '104px' },
    titleContainerStyle: GRID_SLOT_TITLE_STYLE,
    contentContainerStyle: { paddingTop: spacing['12px'], paddingBottom: spacing['12px'] },
  },
})

export interface AuroraCareOptionCardProps {
  careOption: AuroraCareOption
  formattedMessageValues?: Record<string, any>
  onPress?: () => void
  buttonTextOverride?: React.ReactNode
  modalContent?: ProgramDetailsContent
  showExternalLinkIcon?: boolean
  gridSlotSize?: CareOptionsGridSlotSize
  heroCtaSection?: React.ReactNode
  fullWidth?: boolean
}

export const AuroraCareOptionCard: FunctionComponent<AuroraCareOptionCardProps> = ({
  careOption,
  formattedMessageValues,
  onPress,
  buttonTextOverride,
  modalContent,
  showExternalLinkIcon = false,
  gridSlotSize,
  heroCtaSection,
  fullWidth = false,
}) => {
  const { assets, colors, spacing } = useTheme() as ThemeType
  const cardMetadata = AuroraCareOptionsMetadata[careOption]
  const { id, title, imageKey, onCardPress } = cardMetadata
  const handlePress = useCallback(() => {
    const pressFn = onPress ?? onCardPress
    pressFn?.()
  }, [onPress, onCardPress])

  const compactImageKeyName = `${imageKey}Compact`
  const hasCompactAssets = isGridSupported(careOption) && assets[compactImageKeyName]
  const heroImageKeyName = `${imageKey}Hero`

  const compactImageUrl = useGetResponsiveAssets(
    hasCompactAssets
      ? assets[compactImageKeyName].assetNames
      : assets[heroImageKeyName]?.assetNames ?? { defaultAsset: '' },
  )

  const isImageGridSlot =
    gridSlotSize != null &&
    gridSlotSize !== CareOptionsGridSlotSize.HERO &&
    gridSlotSize !== CareOptionsGridSlotSize.CAROUSEL
  const isHeroGridSlot =
    gridSlotSize === CareOptionsGridSlotSize.HERO || gridSlotSize === CareOptionsGridSlotSize.CAROUSEL

  const isDBTIntake = careOption === AuroraCareOption.DBT_INTAKE
  const dbtCtaSection = useMemo(() => {
    if (!isDBTIntake) return undefined
    const { buttonText: dbtButtonText } = AuroraCareOptionsMetadata[AuroraCareOption.DBT_INTAKE]
    return (
      <PrimaryButton
        text={dbtButtonText ? <FormattedMessage {...dbtButtonText} /> : undefined}
        onPress={handlePress}
        testID={tID('AuroraHeroCard-dbtIntake-button')}
        fullWidth
        leftIcon={
          showExternalLinkIcon ? <ExternalLinkIcon size={24} strokeColor={colors.primaryButtonTextActive} /> : undefined
        }
      />
    )
  }, [isDBTIntake, showExternalLinkIcon, colors.primaryButtonTextActive, handlePress])

  const shouldRenderHeroCard = !isImageGridSlot

  if (shouldRenderHeroCard) {
    // Carousel cards defer their width to the carousel slot (focusedItemWidth), so they always fill the
    // wrapper rather than SplitHeroCard's default cap. No-op for multi-option cards (the slot bounds them);
    // it's what lets a lone card span the full stack footprint. Grid/hero cards keep the default caps.
    if (!modalContent) {
      return (
        <AuroraHeroCard
          careOption={careOption}
          formattedMessageValues={formattedMessageValues}
          onCTAPress={handlePress}
          buttonTextOverride={buttonTextOverride}
          ctaSection={dbtCtaSection ?? heroCtaSection}
          isFullWidth={isHeroGridSlot}
          fillContainerWidth={!gridSlotSize || fullWidth}
        />
      )
    }
    return (
      <ProgramDetailsModal
        renderTrigger={(openModal) => (
          <AuroraHeroCard
            careOption={careOption}
            formattedMessageValues={formattedMessageValues}
            onCTAPress={openModal}
            buttonTextOverride={buttonTextOverride}
            ctaSection={dbtCtaSection ?? heroCtaSection}
            isFullWidth={isHeroGridSlot}
            fillContainerWidth={!gridSlotSize || fullWidth}
          />
        )}
        programContent={modalContent}
        onCtaPress={handlePress}
        testId={`${id}-program-details-modal`}
      />
    )
  }

  const compactCardCustomStyle = gridSlotSize ? getGridSlotStyles(spacing)[gridSlotSize] : undefined
  const compactCardTitleSize = gridSlotSize ? GRID_SLOT_TITLE_SIZES[gridSlotSize] : undefined

  if (!modalContent) {
    return (
      <CompactImageCard
        title={title}
        id={id}
        imageUrl={compactImageUrl}
        onCardPress={handlePress}
        formattedMessageValues={formattedMessageValues}
        customStyle={compactCardCustomStyle}
        titleSize={compactCardTitleSize}
      />
    )
  }

  return (
    <ProgramDetailsModal
      modalTrigger={
        <CompactImageCard
          title={title}
          id={id}
          imageUrl={compactImageUrl}
          formattedMessageValues={formattedMessageValues}
          customStyle={compactCardCustomStyle}
          titleSize={compactCardTitleSize}
        />
      }
      programContent={modalContent}
      onCtaPress={handlePress}
      testId={`${id}-program-details-modal`}
    />
  )
}
